import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PinButton, PinnableCardContext, useReportCardMode } from '../crm/reportCards';
import { gsap } from 'gsap';
import { ArrowLeft, ArrowUpRight, ChevronDown, Search, X } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';

/**
 * "Glass" CRM page language (Nov 2026 test on /crm/lost-leads-reports):
 * BBA red / black / off-white chrome (red pills, KPI chips, card markers, CTA)
 * over translucent 28px cards, with a multicolour palette kept for charts and
 * table badges, and pill switchers whose highlight slides with a
 * leading-edge-first clip-path animation. Dark = black, light = off-white.
 */

// BBA brand red (matches functions/brandedEmailLayout.js + PortalBackground); black #0d0b09 / ink #1C1916, off-white #F4F2EF
export const GLASS_ACCENT = '#F80707';

// Data palette: brand red first, then bright distinct hues that read on both backdrops
export const GLASS_PALETTE = ['#F80707', '#FFB547', '#5BC0EB', '#7FD9B0', '#B9A3F5', '#F28BA8', '#93B4F5', '#FF7A68', '#6FCFCF', '#E8C88A', '#C9D87A', '#D8A3E8', '#A6E3C8', '#F5B58A'];

export const GLASS_TONE = {
  red: '#F80707',
  coral: '#FF7A68',
  amber: '#FFB547',
  mint: '#7FD9B0',
  blue: '#5BC0EB',
  lavender: '#B9A3F5',
  stone: '#857C73',
};

// Spelled out so Tailwind's JIT keeps every class
export const GLASS_TEXT = {
  red: 'text-[#E00606] dark:text-[#FF4D40]',
  coral: 'text-[#D9452F] dark:text-[#FF7A68]',
  amber: 'text-[#B7790F] dark:text-[#FFB547]',
  mint: 'text-emerald-600 dark:text-[#7FD9B0]',
  blue: 'text-[#1F86B8] dark:text-[#5BC0EB]',
  stone: 'text-[#857C73] dark:text-[#A89F95]',
  muted: 'text-gray-500 dark:text-white/[0.45]',
  strong: 'text-gray-900 dark:text-white',
};

const BADGE_TONES = {
  red: 'bg-[#F80707] text-white',
  coral: 'bg-[#FF7A68] text-white',
  amber: 'bg-[#FFB547] text-gray-900',
  mint: 'bg-[#7FD9B0] text-gray-900',
  blue: 'bg-[#5BC0EB] text-gray-900',
  lavender: 'bg-[#B9A3F5] text-gray-900',
  neutral: 'bg-black/[0.06] text-gray-700 dark:bg-white/[0.08] dark:text-white/70',
};

const SOFT_BADGE_TONES = {
  red: 'bg-[#F80707]/10 text-[#D10606] dark:bg-[#F80707]/[0.18] dark:text-[#FF6B5E]',
  coral: 'bg-[#FF7A68]/[0.15] text-[#C2412C] dark:bg-[#FF7A68]/[0.15] dark:text-[#FF9A8A]',
  amber: 'bg-[#FFB547]/20 text-[#94600A] dark:bg-[#FFB547]/[0.15] dark:text-[#FFB547]',
  mint: 'bg-[#7FD9B0]/25 text-emerald-800 dark:bg-[#7FD9B0]/[0.15] dark:text-[#7FD9B0]',
  blue: 'bg-[#5BC0EB]/20 text-[#17698F] dark:bg-[#5BC0EB]/[0.15] dark:text-[#5BC0EB]',
  lavender: 'bg-[#B9A3F5]/25 text-[#5B43A8] dark:bg-[#B9A3F5]/[0.15] dark:text-[#B9A3F5]',
  stone: 'bg-[#857C73]/[0.15] text-[#6E665E] dark:bg-[#857C73]/25 dark:text-[#C9BFB4]',
  neutral: 'bg-black/[0.05] text-gray-600 dark:bg-white/[0.06] dark:text-white/60',
};

export const GLASS_FIELD = 'w-full rounded-xl border border-black/10 bg-white/70 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#F80707]/50 disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:placeholder:text-white/30';

export const GLASS_TH = 'pb-3 pr-4 text-left text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-white/40';

export const GLASS_ROW = 'border-t border-black/[0.06] transition-colors hover:bg-black/[0.025] dark:border-white/[0.06] dark:hover:bg-white/[0.035]';

export const GLASS_INSET = 'rounded-2xl border border-black/[0.05] bg-black/[0.03] dark:border-white/[0.06] dark:bg-white/[0.035]';

const CARD = 'relative rounded-[28px] border border-white/80 bg-[#FBFAF8]/70 shadow-[0_10px_40px_-18px_rgba(28,25,22,0.22),inset_0_1px_0_rgba(255,255,255,0.8)] backdrop-blur-2xl dark:border-white/[0.08] dark:bg-[#1C1916]/50 dark:bg-gradient-to-b dark:from-white/[0.05] dark:to-white/[0.01] dark:shadow-[0_24px_60px_-28px_rgba(0,0,0,0.75),inset_0_1px_0_rgba(255,255,255,0.06)]';

// Keyframes for the page: card rise-in and the slow backdrop drift
export const GlassStyles = () => (
  <style>{`
    @keyframes glass-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
    @keyframes glass-drift-a { 0%, 100% { transform: translate(0, 0) scale(1); } 50% { transform: translate(4vw, 3vh) scale(1.08); } }
    @keyframes glass-drift-b { 0%, 100% { transform: translate(0, 0) scale(1.05); } 50% { transform: translate(-5vw, -4vh) scale(0.95); } }
    .glass-rise { animation: glass-rise 0.7s cubic-bezier(0.2, 0.7, 0.2, 1) both; }
    .glass-drift-a { animation: glass-drift-a 26s ease-in-out infinite; }
    .glass-drift-b { animation: glass-drift-b 32s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) {
      .glass-rise, .glass-drift-a, .glass-drift-b { animation: none; }
    }
  `}</style>
);

// BBA backdrop: off-white (light) or black (dark) with soft red glows, fixed behind the page
export const GlassBackdrop = () => (
  <div aria-hidden="true" className="pointer-events-none fixed inset-0 overflow-hidden bg-[#F4F2EF] dark:bg-[#0d0b09]">
    <div className="glass-drift-a absolute -top-[25%] left-[20%] h-[75vh] w-[70vw] rounded-full bg-[#F80707]/[0.07] blur-[120px] dark:bg-[#F80707]/[0.16]" />
    <div className="glass-drift-b absolute top-[5%] -left-[20%] h-[65vh] w-[55vw] rounded-full bg-[#E9E4DD] blur-[120px] dark:bg-[#2A2420]/70" />
    <div className="glass-drift-a absolute -bottom-[30%] -right-[15%] h-[75vh] w-[60vw] rounded-full bg-[#F80707]/[0.09] blur-[140px] dark:bg-[#C90505]/20" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_20%,transparent_35%,rgba(28,25,22,0.08)_100%)] dark:bg-[radial-gradient(ellipse_at_50%_20%,transparent_30%,rgba(0,0,0,0.6)_100%)]" />
  </div>
);

// Inside a PinnableCard the outermost GlassCard owns the pin: a corner pin here, or the
// header's pin (GlassCardHeader). Nested GlassCards get none.
export const GlassCard = ({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) => {
  const pin = useContext(PinnableCardContext);
  const ownsPin = !!pin && !pin.insideCard;
  const inner = useMemo(() => (ownsPin ? { ...pin, insideCard: true } : null), [ownsPin, pin]);
  return (
    <Tag className={`${CARD} glass-rise ${className}`} style={{ animationDelay: `${delay}ms` }} {...rest}>
      <PinnableCardContext.Provider value={inner}>
        {ownsPin && pin.placement === 'corner' && <PinButton className="absolute right-2.5 top-2.5 z-10" />}
        {children}
      </PinnableCardContext.Provider>
    </Tag>
  );
};

// Outlined circle button, used for the back arrow and card "open" arrows
export const GlassCircleButton = ({ to, onClick, title, children, className = '' }) => {
  const cls = `grid h-10 w-10 flex-shrink-0 place-items-center rounded-full border border-black/10 bg-white/40 text-gray-700 transition hover:border-[#F80707]/50 hover:bg-[#F80707]/[0.06] hover:text-[#F80707] dark:border-white/[0.15] dark:bg-white/[0.04] dark:text-white/70 dark:hover:border-[#F80707]/60 dark:hover:bg-[#F80707]/10 dark:hover:text-[#FF4D40] ${className}`;
  if (to) return <Link to={to} title={title} aria-label={title} className={cls}>{children}</Link>;
  return <button type="button" onClick={onClick} title={title} aria-label={title} className={cls}>{children}</button>;
};

export const GlassCardHeader = ({ title, to, toTitle, right, className = '' }) => {
  const pin = useContext(PinnableCardContext);
  const showPin = pin?.placement === 'header';
  return (
    <div className={`flex items-center justify-between gap-3 ${className}`}>
      <h2 className="flex items-center gap-2.5 text-[15px] font-medium text-[#1C1916] dark:text-white/90">
        <span className="h-4 w-1 rounded-full bg-[#F80707]" />
        {title}
      </h2>
      {(right || to || showPin) && (
        <div className="flex items-center gap-2">
          {right}
          {to && (
            <GlassCircleButton to={to} title={toTitle || `Open ${title}`}>
              <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
            </GlassCircleButton>
          )}
          {showPin && <PinButton className="-mr-1.5" />}
        </div>
      )}
    </div>
  );
};

// Big figure with a muted caption, like "$439,011 won, last 6 months"
export const GlassFigure = ({ value, caption, className = '' }) => (
  <div className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 ${className}`}>
    <span className="text-4xl font-medium tracking-tight text-gray-900 tabular-nums dark:text-white">{value}</span>
    {caption && <span className="text-xs text-gray-500 dark:text-white/[0.45]">{caption}</span>}
  </div>
);

// Header KPI: outlined icon circle, value with optional badge, short caption
export const GlassStat = ({ icon: Icon, value, label, badge, badgeTone = 'red', valueClassName = '' }) => (
  <div className="flex items-center gap-3">
    <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-full border border-[#F80707]/25 bg-[#F80707]/[0.06] text-[#F80707] dark:border-[#F80707]/40 dark:bg-[#F80707]/10 dark:text-[#FF4D40]">
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
    </span>
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className={`text-xl font-semibold tabular-nums ${valueClassName || 'text-gray-900 dark:text-white'}`}>{value}</span>
        {badge && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${BADGE_TONES[badgeTone] || BADGE_TONES.neutral}`}>{badge}</span>}
      </div>
      <div className="max-w-[10rem] text-xs leading-snug text-gray-500 dark:text-white/[0.45]">{label}</div>
    </div>
  </div>
);

// Legend chip: dot, label, value
export const GlassChip = ({ color, label, value }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-black/[0.05] bg-black/[0.04] px-3 py-1.5 text-xs dark:border-white/[0.06] dark:bg-white/[0.06]">
    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
    <span className="font-medium text-gray-800 dark:text-white/[0.85]">{label}</span>
    {value !== undefined && <span className="text-gray-500 tabular-nums dark:text-white/[0.45]">{value}</span>}
  </span>
);

export const GlassBadge = ({ tone = 'neutral', children }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${SOFT_BADGE_TONES[tone] || SOFT_BADGE_TONES.neutral}`}>
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {children}
  </span>
);

export const GlassAccentButton = ({ children, className = '', ...rest }) => (
  <button
    type="button"
    className={`inline-flex items-center gap-2 rounded-full bg-[#F80707] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_-10px_rgba(248,7,7,0.75)] transition hover:bg-[#C90505] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none ${className}`}
    {...rest}
  >
    {children}
  </button>
);

const prefersReducedMotion = () => typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Pill switcher. A clipped highlight layer (with its own copy of the labels)
 * slides between options; the leading edge moves first and faster, the
 * trailing edge catches up, so the pill stretches then settles.
 */
export const SlidingPills = ({ options, value, onChange, size = 'md', className = '', ariaLabel }) => {
  const boxRef = useRef(null);
  const pillRef = useRef(null);
  const btnRefs = useRef({});
  const edges = useRef(null);

  const itemCls = size === 'sm' ? 'px-3.5 py-1.5 text-xs' : 'px-5 py-2 text-sm';

  const place = useCallback((animate) => {
    const box = boxRef.current;
    const pill = pillRef.current;
    const target = btnRefs.current[value];
    if (!box || !pill || !target) return;
    const W = box.scrollWidth, H = box.clientHeight;
    const L = target.offsetLeft;
    const R = W - (target.offsetLeft + target.offsetWidth);
    const T = target.offsetTop;
    const B = H - (target.offsetTop + target.offsetHeight);
    const clip = (l, r) => `inset(${T}px ${r}px ${B}px ${l}px round 999px)`;
    const o = edges.current;
    if (!o || !animate || prefersReducedMotion()) {
      if (o) gsap.killTweensOf(o);
      edges.current = { l: L, r: R };
      gsap.set(pill, { clipPath: clip(L, R) });
      return;
    }
    gsap.killTweensOf(o);
    const right = L > o.l;
    // the leading edge goes first and faster; the trailing edge catches up
    gsap.to(o, {
      l: L, duration: right ? 0.55 : 0.36,
      ease: right ? 'power3.inOut' : 'power3.out',
      onUpdate: () => gsap.set(pill, { clipPath: clip(o.l, o.r) }),
    });
    gsap.to(o, {
      r: R, duration: right ? 0.36 : 0.55,
      ease: right ? 'power3.out' : 'power3.inOut',
      onUpdate: () => gsap.set(pill, { clipPath: clip(o.l, o.r) }),
    });
  }, [value]);

  const placeRef = useRef(place);
  placeRef.current = place;

  useLayoutEffect(() => { place(true); }, [place]);

  // Snap (no animation) on layout changes; observed once so it never interrupts a slide
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => placeRef.current(false));
    ro.observe(box);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => () => { if (edges.current) gsap.killTweensOf(edges.current); }, []);

  return (
    <div
      ref={boxRef}
      role="tablist"
      aria-label={ariaLabel}
      className={`relative inline-flex items-center rounded-full border border-black/[0.06] bg-black/[0.04] p-1 backdrop-blur-xl dark:border-white/[0.08] dark:bg-white/[0.05] ${className}`}
    >
      {options.map(opt => (
        <button
          key={opt.value}
          ref={el => { btnRefs.current[opt.value] = el; }}
          type="button"
          role="tab"
          aria-selected={opt.value === value}
          onClick={() => onChange(opt.value)}
          className={`relative whitespace-nowrap rounded-full font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-white/60 dark:hover:text-white ${itemCls}`}
        >
          {opt.label}
        </button>
      ))}
      <div
        ref={pillRef}
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center bg-[#F80707] p-1 text-white"
        style={{ clipPath: 'inset(0 100% 0 0)' }}
      >
        {options.map(opt => (
          <span key={opt.value} className={`whitespace-nowrap rounded-full font-medium ${itemCls}`}>{opt.label}</span>
        ))}
      </div>
    </div>
  );
};

// Recharts colours per theme (recharts needs real values, not classes)
export const glassChartTheme = (darkMode) => (darkMode
  ? { grid: 'rgba(255,255,255,0.07)', tick: 'rgba(255,255,255,0.45)', track: 'rgba(255,255,255,0.035)', cursor: 'rgba(255,255,255,0.04)', ringBg: 'rgba(255,255,255,0.06)' }
  : { grid: 'rgba(30,20,10,0.08)', tick: 'rgba(40,30,20,0.5)', track: 'rgba(30,20,10,0.04)', cursor: 'rgba(30,20,10,0.04)', ringBg: 'rgba(30,20,10,0.06)' });

export const GlassTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-2xl border border-black/[0.06] bg-white/[0.85] px-3 py-2 text-xs shadow-lg backdrop-blur-xl dark:border-white/10 dark:bg-[#14110f]/90">
      {label !== undefined && label !== '' && <div className="mb-1 text-gray-500 dark:text-white/[0.45]">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.payload?.color || p.payload?.fill || p.color }} />
          <span className="text-gray-700 dark:text-white/75">{p.name}</span>
          <span className="ml-auto pl-3 font-semibold text-gray-900 tabular-nums dark:text-white">{p.value}{unit ? ` ${unit}` : ''}</span>
        </div>
      ))}
    </div>
  );
};

// Pastel initials circle, colour picked from the name so it stays stable
export const GlassAvatar = ({ name }) => {
  const clean = (name || '').trim();
  const initials = clean
    ? clean.split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : '?';
  let hash = 0;
  for (let i = 0; i < clean.length; i += 1) hash = (hash * 31 + clean.charCodeAt(i)) % 997;
  const bg = ['#FDD5D0', '#FFE6BF', '#CDEBF8', '#CFF1E2', '#E3DBFB', '#FAD5E1', '#D6E3FB'][hash % 7];
  return (
    <span className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full text-[11px] font-semibold text-[#1C1916]" style={{ backgroundColor: bg }}>
      {initials}
    </span>
  );
};

export const GLASS_SECONDARY_BUTTON = 'inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/40 px-4 py-2 text-sm font-medium text-gray-700 transition hover:border-[#F80707]/40 hover:text-[#F80707] disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-white/[0.04] dark:text-white/75 dark:hover:border-[#F80707]/50 dark:hover:text-[#FF4D40]';

// Modals: render the overlay OUTSIDE any GlassCard (backdrop-filter traps fixed children)
export const GLASS_MODAL_OVERLAY = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm';
export const GLASS_MODAL_PANEL = 'relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-[28px] border border-white/80 bg-[#FBFAF8]/95 p-6 text-gray-900 shadow-2xl backdrop-blur-2xl dark:border-white/10 dark:bg-[#14110f]/95 dark:text-white';

// Page shell: keyframes + fixed backdrop + full-width padded content.
// `backdrop` swaps the glass backdrop for another (e.g. <PortalBackground />).
// While a report renders off-screen for /crm/reports My view, only its cards matter: no backdrop.
export const GlassPage = ({ className = '', backdrop = <GlassBackdrop />, children }) => {
  const cardMode = useReportCardMode();
  return (
    <div className="relative min-h-screen w-full">
      <GlassStyles />
      {!cardMode && backdrop}
      <div className={`relative w-full p-4 md:p-6 lg:p-8 ${className}`}>{children}</div>
    </div>
  );
};

/**
 * Page header: back button + centred switcher (e.g. SlidingPills tabs) on top,
 * then the big two-line title, inline GlassStat KPIs and right-hand controls.
 * `statsKey` re-keys the stats so they replay their rise when a tab changes.
 */
export const GlassPageHeader = ({ title, backTo, onBack, backTitle = 'Back', center, stats = [], statsKey = '', controls }) => (
  <>
    {(backTo || onBack || center) && (
      <div className="glass-rise grid grid-cols-[auto_1fr_auto] items-center gap-3 sm:grid-cols-[1fr_auto_1fr]">
        <div>
          {(backTo || onBack) && (
            <GlassCircleButton to={backTo} onClick={onBack} title={backTitle}>
              <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            </GlassCircleButton>
          )}
        </div>
        <div className="justify-self-center">{center}</div>
        <div />
      </div>
    )}
    <div className={`mb-8 flex flex-wrap items-center gap-x-10 gap-y-6 ${backTo || onBack || center ? 'mt-8' : ''}`}>
      <h1 className="glass-rise text-4xl font-medium leading-[1.05] tracking-tight text-gray-900 dark:text-white">{title}</h1>
      {stats.map((s, i) => (
        <div key={`${statsKey}-${s.key || s.label}`} className="glass-rise" style={{ animationDelay: `${60 + i * 50}ms` }}>
          <GlassStat {...s} />
        </div>
      ))}
      {controls && (
        <div className="glass-rise flex flex-wrap items-center gap-3 2xl:ml-auto" style={{ animationDelay: '120ms' }}>
          {controls}
        </div>
      )}
    </div>
  </>
);

// Search box; clearing calls onChange with { target: { value: '' } } like a native input
export const GlassSearch = ({ value, onChange, placeholder = 'Search…', className = '', ...rest }) => (
  <div className={`relative ${className}`}>
    <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-white/40" />
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="w-full rounded-full border border-black/[0.06] bg-white/50 py-2.5 pl-11 pr-10 text-sm text-gray-900 placeholder:text-gray-400 backdrop-blur-xl focus:outline-none focus:ring-2 focus:ring-[#F80707]/40 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-white dark:placeholder:text-white/30"
      {...rest}
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange({ target: { value: '' } })}
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 transition hover:text-[#F80707] dark:text-white/40"
        aria-label="Clear search"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

// Pill-shaped native select for filters with more options than SlidingPills suits
export const GlassSelect = ({ className = '', children, ...rest }) => (
  <div className={`relative inline-flex ${className}`}>
    <select
      className="w-full appearance-none rounded-full border border-black/[0.06] bg-white/50 py-2 pl-4 pr-9 text-sm font-medium text-gray-700 backdrop-blur-xl focus:outline-none focus:ring-2 focus:ring-[#F80707]/40 dark:border-white/[0.08] dark:bg-white/[0.05] dark:text-white/80 dark:[&>option]:bg-[#14110f] dark:[&>option]:text-white"
      {...rest}
    >
      {children}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-white/50" />
  </div>
);

export const GlassEmpty = ({ className = '', children }) => (
  <div className={`flex items-center justify-center text-center text-sm text-gray-500 dark:text-white/40 ${className}`}>{children}</div>
);

// Donut ring with a faint full-circle track behind the coloured segments
export const GlassDonut = ({ data, dataKey, nameKey, inner, outer, darkMode, unit, children }) => {
  const chart = glassChartTheme(darkMode);
  return (
    <div className="relative h-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={[{ v: 1 }]} dataKey="v" innerRadius={inner} outerRadius={outer} fill={chart.ringBg} stroke="none" isAnimationActive={false} />
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={nameKey}
            innerRadius={inner}
            outerRadius={outer}
            paddingAngle={data.length > 1 ? 3 : 0}
            cornerRadius={8}
            stroke="none"
            animationDuration={800}
          >
            {data.map((entry, idx) => (
              <Cell key={`d-${idx}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<GlassTooltip unit={unit} />} />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">{children}</div>
    </div>
  );
};

// Horizontal bar breakdown (reasons / stages) on a rounded track
export const GlassBreakdownCard = ({ title, data, labelKey, emptyText, darkMode, delay, unit = 'leads', valueKey = 'count' }) => {
  const chart = glassChartTheme(darkMode);
  return (
    <GlassCard className="p-5" delay={delay}>
      <GlassCardHeader title={title} />
      <div className="mt-4 h-72">
        {data.length === 0 ? (
          <GlassEmpty className="h-full">{emptyText}</GlassEmpty>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 8, left: 0, bottom: 0 }} barCategoryGap="24%">
              <CartesianGrid horizontal={false} stroke={chart.grid} strokeDasharray="4 6" />
              <XAxis type="number" allowDecimals={false} tick={{ fill: chart.tick, fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis dataKey={labelKey} type="category" width={130} tick={{ fill: chart.tick, fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: chart.cursor }} content={<GlassTooltip unit={unit} />} />
              <Bar dataKey={valueKey} name="Count" radius={[0, 12, 12, 0]} maxBarSize={30} background={{ fill: chart.track, radius: 12 }} animationDuration={700}>
                {data.map((entry, idx) => (
                  <Cell key={`b-${idx}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </GlassCard>
  );
};
